/**
 * PlanSelectionModal - Modal de sélection de plan AI Credits
 * 
 * Ce composant est conçu pour faciliter l'intégration future de Stripe.
 * 
 * UTILISATION ACTUELLE (sans paiement) :
 * ```tsx
 * <PlanSelectionModal
 *   id="planModal"
 *   selectedPlan="ESSENTIAL"
 *   config={systemConfig}
 *   onClose={() => setSelectedPlan(null)}
 * />
 * ```
 * → Affiche un message "Contactez-nous" quand l'utilisateur clique sur "Souscrire"
 * 
 * UTILISATION FUTURE (avec Stripe) :
 * ```tsx
 * const handlePaymentRequest = async (plan: string, planDetails: any) => {
 *   // Créer session Stripe Checkout
 *   const response = await fetch('/api/v1/payment/checkout/session', {
 *     method: 'POST',
 *     body: JSON.stringify({ plan, company_id: currentCompany.id })
 *   });
 *   const { checkout_url } = await response.json();
 *   
 *   // Rediriger vers Stripe
 *   window.location.href = checkout_url;
 * };
 * 
 * <PlanSelectionModal
 *   id="planModal"
 *   selectedPlan="ESSENTIAL"
 *   config={systemConfig}
 *   onClose={() => setSelectedPlan(null)}
 *   onPaymentRequest={handlePaymentRequest}  // ← Ajouter cette prop
 * />
 * ```
 * → Redirige automatiquement vers Stripe Checkout
 * 
 * @see architecture-documentation/FAMLA-AI-CREDITS-ANALYSE-FINALE-MANAGER.md
 * @see architecture-documentation/IMPLEMENTATION-AI-CREDITS-SPRINT-1-4.md
 */

import React, { useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { tenantBranding } from '../../services/config';

interface SystemConfig {
    free_credits: number;
    essential_credits: number;
    essential_price: number;
    plus_credits_monthly: number;
    plus_price_monthly: number;
    tokens_per_credit: number;
    rollover_percentage: number;
    rollover_max_credits: number;
    topup_essential_credits: number;
    topup_essential_price: number;
    grace_period_days: number;
}

interface PlanSelectionModalProps {
    id: string;
    selectedPlan: string;
    config: SystemConfig;
    onClose?: () => void;
    /**
     * Callback optionnel pour gérer le paiement
     * Si fourni, sera appelé quand l'utilisateur clique sur "Souscrire"
     * Pour intégration Stripe future : passer une fonction qui crée la session checkout
     * 
     * @param plan - Plan sélectionné ('ESSENTIAL' | 'PLUS')
     * @param planDetails - Détails du plan (prix, crédits, etc.)
     * @returns Promise<void> - Résout quand le paiement est initié
     * 
     * Exemple pour Stripe :
     * ```typescript
     * onPaymentRequest={async (plan, details) => {
     *   const session = await createStripeCheckoutSession(plan);
     *   window.location.href = session.url;
     * }}
     * ```
     * 
     * ═══════════════════════════════════════════════════════════════════════════════
     * [POINTS À NOTER POUR INTÉGRATION STRIPE]
     * ═══════════════════════════════════════════════════════════════════════════════
     * 
     * 1. BACKEND - Endpoint Checkout Session :
     *    - Créer endpoint POST /api/v1/payment/checkout/session
     *    - Prendre en paramètre : plan (ESSENTIAL | PLUS), company_id
     *    - Créer session Stripe Checkout (one-time pour ESSENTIAL, subscription pour PLUS)
     *    - Retourner checkout_url pour redirection
     * 
     * 2. BACKEND - Webhook Stripe :
     *    - Créer endpoint POST /api/v1/payment/webhook/stripe
     *    - Gérer événements : checkout.session.completed, customer.subscription.updated, etc.
     *    - Après paiement réussi :
     *      a) Mettre à jour AICreditAccount.plan
     *      b) Ajouter les crédits (ESSENTIAL: +40, PLUS: +100)
     *      c) Créer CreditTransaction (TOPUP ou SUBSCRIPTION)
     *      d) Appeler sync_company_status_from_plan(company_id) pour synchroniser Company.status
     * 
     * 3. FRONTEND - Fonction handlePaymentRequest :
     *    - Dans PricingView.tsx, créer fonction qui appelle l'endpoint checkout
     *    - Passer cette fonction comme prop onPaymentRequest au modal
     *    - Exemple :
     *      ```typescript
     *      const handlePaymentRequest = async (plan: string, planDetails: any) => {
     *        const response = await fetch('/api/v1/payment/checkout/session', {
     *          method: 'POST',
     *          headers: { 'Authorization': `Bearer ${auth.access_token}` },
     *          body: JSON.stringify({ plan, company_id: auth.session.company_id })
     *        });
     *        const { checkout_url } = await response.json();
     *        window.location.href = checkout_url;
     *      };
     *      ```
     * 
     * 4. SYNCHRONISATION Company.status :
     *    - Le modal ne gère PAS directement la synchronisation
     *    - La synchronisation est gérée par le backend via :
     *      - Webhook Stripe → changement plan → sync_company_status_from_plan()
     *      - Endpoint changement plan → sync_company_status_from_plan()
     *    - Company.status sera automatiquement mis à jour : FREE → ESSENTIAL/PLUS/ENTERPRISE
     * 
     * 5. GESTION ERREURS :
     *    - Le modal affiche déjà les erreurs via alert()
     *    - Pour Stripe, gérer les cas : paiement échoué, session expirée, etc.
     * 
     * ═══════════════════════════════════════════════════════════════════════════════
     */
    onPaymentRequest?: (plan: string, planDetails: {
        name: string;
        price: string;
        credits: string;
        description: string;
    }) => Promise<void> | void;
}

const PlanSelectionModal: React.FC<PlanSelectionModalProps> = ({
    id,
    selectedPlan,
    config,
    onClose,
    onPaymentRequest
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    // Fonction helper pour remplacer les placeholders dans les traductions
    const replacePlaceholders = (text: string, values: Record<string, number | string>): string => {
        let result = text;
        Object.keys(values).forEach(key => {
            result = result.replace(new RegExp(`{{${key}}}`, 'g'), String(values[key]));
        });
        return result;
    };
    const closeRef = useRef<HTMLButtonElement>(null);
    const modalRef = useRef<HTMLDivElement>(null);

    const handleChoosePlan = async () => {
        if (selectedPlan === 'ENTERPRISE') {
            // ENTERPRISE : Redirection vers page contact
            if (closeRef.current) {
                closeRef.current.click();
            }
            if (onClose) {
                onClose();
            }
            navigate('/contact');
            return;
        }

        // ESSENTIAL ou PLUS : Gestion du paiement
        if (onPaymentRequest) {
            // ✅ Intégration paiement fournie (ex: Stripe)
            // Celui qui intègre Stripe passera cette fonction
            try {
                await onPaymentRequest(selectedPlan, planDetails);
                // Fermer le modal après initiation du paiement
                if (closeRef.current) {
                    closeRef.current.click();
                }
                if (onClose) {
                    onClose();
                }
            } catch (error) {
                console.error('[PlanSelectionModal] Payment request error:', error);
                // Afficher erreur à l'utilisateur
                alert(t('views.modal.planSelection.paymentError', 'Erreur lors de l\'initiation du paiement. Veuillez réessayer.'));
            }
        } else {
            // ⚠️ Pas d'intégration paiement : Redirection vers page contact
            // Mode temporaire jusqu'à intégration Stripe
            if (closeRef.current) {
                closeRef.current.click();
            }
            if (onClose) {
                onClose();
            }
            navigate('/contact');
        }
    };

    const handleClose = useCallback(() => {
        if (onClose) {
            onClose();
        }
        document.body.style.overflow = '';
    }, [onClose]);

    useEffect(() => {
        const modalElement = modalRef.current;
        if (modalElement) {
            const handleModalHidden = () => {
                handleClose();
            };
            modalElement.addEventListener('hidden.bs.modal', handleModalHidden);
            document.body.style.overflow = 'hidden';

            // Ouvrir le modal automatiquement
            const bootstrap = (window as any).bootstrap;
            if (bootstrap) {
                const modal = bootstrap.Modal.getOrCreateInstance(modalElement);
                modal.show();
            }

            return () => {
                modalElement.removeEventListener('hidden.bs.modal', handleModalHidden);
                document.body.style.overflow = '';
            };
        }
    }, [handleClose]);

    const getPlanDetails = () => {
        switch (selectedPlan) {
            case 'ESSENTIAL':
                return {
                    name: t('views.pricing.plans.essential.name'),
                    price: `$${config.essential_price}`,
                    credits: `${config.essential_credits} ${t('views.pricing.units.credits')}`,
                    description: t(
                        'views.modal.planSelection.essential.description',
                        'Achat ponctuel. Pas d\'expiration, top-up disponible à tout moment.',
                    ),
                };
            case 'PLUS':
                return {
                    name: t('views.pricing.plans.plus.name'),
                    price: `$${config.plus_price_monthly}/${t('views.pricing.units.month')}`,
                    credits: `${config.plus_credits_monthly} ${t('views.pricing.units.creditsPerMonth')}`,
                    description: replacePlaceholders(
                        t(
                            'views.modal.planSelection.plus.description',
                            'Souscription mensuelle. Roll-over {{percentage}}% des crédits non utilisés (max {{max}} crédits).',
                        ),
                        {
                            percentage: config.rollover_percentage,
                            max: config.rollover_max_credits,
                        },
                    ),
                };
            case 'ENTERPRISE':
                return {
                    name: t('views.pricing.plans.enterprise.name'),
                    price: t('views.pricing.units.custom'),
                    credits: t('views.pricing.units.unlimited'),
                    description: t('views.modal.planSelection.enterprise.description', 'Forfait personnalisé avec crédits illimités, support dédié et SLA premium. Contactez-nous pour un devis.')
                };
            default:
                return {
                    name: selectedPlan,
                    price: '',
                    credits: '',
                    description: ''
                };
        }
    };

    const planDetails = getPlanDetails();

    const modalContent = (
        <div
            ref={modalRef}
            className="modal fade"
            data-bs-backdrop="true"
            data-bs-keyboard="true"
            id={id}
            tabIndex={-1}
            aria-labelledby={`${id}Label`}
            aria-hidden="true"
            style={{ zIndex: 10000, position: 'fixed' }}
        >
            <div className="modal-dialog modal-dialog-centered" style={{ zIndex: 10001, pointerEvents: 'auto' }}>
                <div className="modal-content" style={{ zIndex: 10002, pointerEvents: 'auto', backgroundColor: '#1a1a1a' }}>
                    <div className="modal-header border-secondary">
                        <h5 className="modal-title text-theme" id={`${id}Label`}>
                            💳 {t('views.modal.planSelection.title', 'Choisir le plan')} {planDetails.name}
                        </h5>
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            data-bs-dismiss="modal"
                            aria-label={t('words.close')}
                            onClick={handleClose}
                            style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                        ></button>
                    </div>
                    <div className="modal-body text-white">
                        <div className="mb-3">
                            <h6 className="text-theme">{t('views.modal.planSelection.planDetails', 'Détails du plan')}</h6>
                            <div className="p-3 bg-dark border border-secondary rounded">
                                <div className="d-flex justify-content-between mb-2">
                                    <span>{t('views.modal.planSelection.plan', 'Plan')}:</span>
                                    <strong className="text-theme text-uppercase">{planDetails.name}</strong>
                                </div>
                                <div className="d-flex justify-content-between mb-2">
                                    <span>{t('views.modal.planSelection.price', 'Prix')}:</span>
                                    <strong className="text-warning">{planDetails.price}</strong>
                                </div>
                                <div className="d-flex justify-content-between">
                                    <span>{t('views.modal.planSelection.credits', 'Crédits')}:</span>
                                    <strong className="text-info">{planDetails.credits}</strong>
                                </div>
                            </div>
                        </div>
                        {planDetails.description ? (
                            <p className="mb-3">{planDetails.description}</p>
                        ) : null}
                        {selectedPlan === 'ENTERPRISE' ? (
                            <div className="alert alert-info">
                                <i className="fa-solid fa-envelope me-2"></i>
                                {t('views.modal.planSelection.enterprise.contact', 'Contactez-nous pour un devis personnalisé', { supportEmail: tenantBranding.supportEmail })}
                            </div>
                        ) : (
                            <div className={`alert ${onPaymentRequest ? 'alert-info' : 'alert-warning'}`}>
                                <i className="fa-solid fa-info-circle me-2"></i>
                                {onPaymentRequest 
                                    ? t('views.modal.planSelection.paymentReady', 'Vous serez redirigé vers la page de paiement sécurisée.')
                                    : t('views.modal.planSelection.contactUsInfo', 'Pour souscrire, vous serez redirigé vers notre formulaire de contact')
                                }
                            </div>
                        )}
                    </div>
                    <div className="modal-footer border-secondary">
                        <button
                            ref={closeRef}
                            type="button"
                            className="btn btn-secondary"
                            data-bs-dismiss="modal"
                            onClick={handleClose}
                            style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                        >
                            {t('words.close', 'Fermer')}
                        </button>
                        <button
                            type="button"
                            onClick={handleChoosePlan}
                            className="btn btn-primary"
                            style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                        >
                            {selectedPlan === 'ENTERPRISE' 
                                ? t('views.modal.planSelection.contactSales', 'Contacter les ventes')
                                : t('views.modal.planSelection.subscribe', 'Souscrire')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
};

export default PlanSelectionModal;

